<template>
  <!-- 2 options → toggle link (or custom trigger) -->
  <slot
    v-if="options.length === 2 && !forceSelect"
    name="trigger"
    :current-label="currentLabel"
    :current-short-label="currentShortLabel"
    :toggle="toggle"
  >
    <a
      v-tooltip="toggleTooltip"
      class="mode-switch"
      :style="colorStyle"
      @click="toggle"
    >
      <i v-if="currentIcon" :class="currentIcon" />
      <i v-else class="fa-solid fa-arrow-right-arrow-left" />
      <span v-if="!currentIcon">{{ currentLabel }}</span>
    </a>
  </slot>

  <!-- 3+ options or forceSelect → select with hidden native + visible label -->
  <label v-else class="mode-switch is-select" :style="colorStyle">
    <slot
      name="trigger"
      :current-label="currentLabel"
      :current-short-label="currentShortLabel"
    >
      <i v-if="currentIcon" :class="currentIcon" />
      <i v-else class="fa-solid fa-arrow-right-arrow-left" />
      <span>{{ currentLabel }}</span>
      <i class="fa-solid fa-chevron-down caret" />
    </slot>
    <select
      :value="modelValue"
      @change="$emit('update:modelValue', $event.target.value)"
    >
      <option v-for="opt in options" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>
  </label>
</template>

<script>
export default {
  name: 'ModeSwitch',
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
      validator: (v) => v.length >= 2 && v.every((o) => o.value && o.label),
    },
    forceSelect: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    currentLabel() {
      return this.options.find((o) => o.value === this.modelValue)?.label || ''
    },
    currentShortLabel() {
      const opt = this.options.find((o) => o.value === this.modelValue)
      return opt?.shortLabel || opt?.label || ''
    },
    currentIcon() {
      return this.options.find((o) => o.value === this.modelValue)?.icon ?? null
    },
    colorStyle() {
      if (!this.color) return null
      return { '--ms-color': `var(--${this.color})` }
    },
    toggleTooltip() {
      if (this.options.length !== 2 || this.forceSelect) return null
      const [a, b] = this.options
      return {
        content: `<u>${a.label} ⇄ ${b.label}</u><br>Toggle between <em>${a.label}</em> and <em>${b.label}</em>`,
        html: true,
      }
    },
  },
  methods: {
    toggle() {
      const idx = this.options.findIndex((o) => o.value === this.modelValue)
      const next = this.options[(idx + 1) % this.options.length]
      this.$emit('update:modelValue', next.value)
    },
  },
}
</script>

<style lang="scss" scoped>
.mode-switch {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 0.3em;
  font-size: 0.8rem;
  color: var(--ms-color, var(--primary));
  cursor: pointer;
  font-weight: 700;
  border-bottom: 2px dotted currentColor;
  line-height: 1;
  margin-bottom: var(--space-1);

  &:hover {
    filter: brightness(0.85);
  }

  // Default swap icon: small + rotated in select mode
  .fa-arrow-right-arrow-left {
    font-size: 0.6rem;
  }

  &.is-select .fa-arrow-right-arrow-left {
    transform: rotate(90deg);
  }

  .caret {
    font-size: 0.45rem;
  }

  // Overlay the native select on top so clicking anywhere opens it
  &.is-select {
    position: relative;
  }

  select {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
}
</style>
